import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import classes from '../../../pipeline.module.scss';
import Api from '../../../../../../../api/api';
import Table from '../../../../../../_common/Tables/Table';
import TableAndPagination from '../../../../../../_common/TableAndPagination/TableAndPagination';
import Modal from '../../../../../../_common/Modal/Modal';
import Loader from '../../../../../../_common/Loader/Loader';
import { Card } from '../../PipelineComponents';
import { formattedDate } from '../../../pipelineHelpers';

const title = 'Largest gaps - member detail';

const tableHeader = [
  { name: 'Member', path: 'name' },
  { name: 'Medication', path: 'productName' },
  { name: 'Last fill', path: 'lastFill' },
  { name: 'Days supply', path: 'daysSupply' },
  { name: 'Expected refill', path: 'expectedRefill' },
  { name: 'Fill-to-fill', path: 'gapDays' },
  { name: 'Cumulative YTD', path: 'cumulativeGapDays' },
  { name: 'PDC', path: 'pdc' },
];

const modalInitialState = {
  open: false,
  firstLoad: true,
  loading: false,
  list: [],
  totalPage: 1,
  page: 1,
  limit: 15,
};

const formattedMembers = (list) => (list || []).map((d) => ({
  ...d,
  id: d.PatientId,
  name: [d.firstName, d.lastName].filter(Boolean).join(' '),
  lastFill: formattedDate(d.fillDate),
  daysSupply: d.daysSupply?.toString(),
  expectedRefill: formattedDate(d.expectedRefill),
  gapDays: d.gapDays?.toString(),
  pdc: d.pdc ? `${d.pdc}%` : '-',
}));

const GapDaysMembersCard = ({ params }) => {
  const navigate = useNavigate();

  const firstLoad = useRef(true);

  const [membersData, setMembersData] = useState([]);
  const [showSeeMore, showSeeMoreToggle] = useState(false);
  const [loading, loadingToggle] = useState(true);

  const [modal, setModal] = useState(modalInitialState);

  useEffect(() => {
    (async () => {
      try {
        loadingToggle(true);

        const { data } = await Api.getGapDays({ ...params, limit: 10, page: 1 });

        setMembersData(formattedMembers(data?.data?.response));

        showSeeMoreToggle(data?.data?._meta?.pageCount > 1);

        loadingToggle(false);
        firstLoad.current = false;
      } catch (e) {
        console.warn(e);
      }
    })();
  }, [params]);

  useEffect(() => {
    if (!modal.open) return;

    (async () => {
      try {
        setModal((prev) => ({ ...prev, loading: true }));

        const { data } = await Api.getGapDays({
          ...params, page: modal.page, limit: modal.limit,
        });

        setModal((prev) => ({
          ...prev,
          list: formattedMembers(data?.data?.response),
          totalPage: data?.data?._meta?.pageCount || 1,
        }));

        setModal((prev) => ({ ...prev, loading: false, firstLoad: false }));
      } catch (e) {
        console.warn(e);
      }
    })();
  }, [params, modal.open, modal.page, modal.limit]);

  const openModal = () => setModal({ ...modalInitialState, open: true });

  const onChangePageData = (path, value) => {
    setModal((prev) => (path === 'limit'
      ? { ...prev, limit: value, page: 1 }
      : { ...prev, page: value }));
  };

  return (
    <>
      <Card
        title={title}
        caption="Drill-down from any bucket or class above"
        loading={loading}
        firstLoad={firstLoad.current}
        isEmpty={!membersData.length}
      >
        <div className={classes.pipeline_table}>
          <Table
            header={tableHeader}
            data={membersData}
            className={classes.clickable_row}
            onClick={(PatientId) => navigate(`/patients/${PatientId}`)}
          />
        </div>

        {showSeeMore && (
          <div className={classes.see_more_wrapper}>
            <button
              type="button"
              className={classes.see_more_btn}
              onClick={openModal}
            >
              See more
            </button>
          </div>
        )}
      </Card>

      <Modal
        open={modal.open}
        onClose={() => setModal((prev) => ({ ...prev, open: false }))}
        className={classes.members_modal}
      >
        <div className={classes.members_modal_content}>
          <h2>{title}</h2>

          <div className={classes.members_modal_table}>
            {modal.firstLoad
              ? (
                <div className={classes.members_modal_loader}>
                  <Loader />
                </div>
              )

              : (
                <TableAndPagination
                  header={tableHeader}
                  data={modal.list}
                  loading={modal.loading}
                  onChange={onChangePageData}
                  className={classes.clickable_row}
                  onClick={(PatientId) => navigate(`/patients/${PatientId}`)}
                  totalPage={modal.totalPage}
                  page={modal.page - 1}
                  limit={modal.limit}
                />
              )}
          </div>
        </div>
      </Modal>
    </>
  );
};

GapDaysMembersCard.propTypes = {
  params: PropTypes.object.isRequired,
};

export default GapDaysMembersCard;
